/**
 * Capacidade e eficiência em escala.
 *
 * 🛑 **É a tela que decide qual hardware comprar.** As demais respondem se *um*
 * usuário fica satisfeito; esta responde **quantos usuários cabem, a que custo
 * por token e a que custo de energia** — que é o que separa dois candidatos com
 * o mesmo desempenho individual.
 *
 * Nenhuma métrica aqui é opcional: cada uma responde a uma pergunta de
 * dimensionamento que as outras telas não alcançam.
 */

import { useMemo } from "react";
import { ConcurrencyCurveChart, type SeriesPoint } from "../components/charts.js";
import { DataTable, type Column } from "../components/DataTable.js";
import { HintLabel } from "../components/HintLabel.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { VerdictBadge } from "../components/VerdictBadge.js";
import { snapshot } from "../data/source.js";
import { TARGET_TOKENS_PER_SEC } from "../fixtures/scale.js";
import type { Verdict } from "../fixtures/index.js";
import { formatContext, formatNumber } from "../lib/format.js";

/** Cenário de referência: o candidato de compra sob a carga alvo. */
const REFERENCE_SCENARIO_ID = 3;

/**
 * Ocupação do cache de estado acima da qual o número é destacado.
 *
 * Acima de 90% o escalonador começa a despejar contexto, e a vazão cai de forma abrupta,
 * não gradual — é o aviso de que o cenário está no limite.
 */
const KV_ALERT_PCT = 90;

/** O que cada métrica responde — exibido ao pousar o ponteiro sobre o rótulo. */
const METRIC_HINT = {
  aggregate:
    "Vazão somada de todas as requisições ativas. Mede CAPACIDADE do servidor, não latência: um sistema pode ter vazão agregada alta e latência individual ruim.",
  requests:
    "Requisições concluídas por segundo. Responde quantas conversas o servidor fecha por unidade de tempo.",
  maxConc:
    "Concorrência máxima que ainda sustenta o alvo. É o número que responde 'quantos usuários simultâneos este hardware atende'.",
  kv: "Tokens de contexto que cabem no cache de estado. É o teto de concorrência × contexto — passar dele derruba a vazão de forma abrupta, não gradual.",
  perWatt:
    "Tokens gerados por watt. Separa candidatos que empatam em desempenho: ao longo de anos, a diferença de energia pode superar a de preço de compra.",
  costPerM:
    "Custo por milhão de tokens. É a métrica de comparação universal — normaliza hardware de portes diferentes e permite comparar comprar contra alugar.",
  itl: "Latência entre tokens no percentil 95. É a suavidade percebida: latência média boa com variação alta produz texto que engasga.",
  bandwidth:
    "Banda efetivamente exercida sobre a nominal. É o número que sustenta toda extrapolação para outro hardware.",
} as const;

/** Uma linha da comparação de capacidade entre cenários. */
interface Row {
  readonly scenarioId: number;
  readonly label: string;
  readonly concurrency: number;
  readonly contextLen: number;
  readonly verdict: Verdict;
  readonly aggregate: number;
  readonly requests: number;
  readonly maxConc: number;
  readonly kvUsedPct: number;
  readonly perWatt: number;
  readonly costPerM: number;
  readonly itl: number;
  readonly bandwidthPct: number;
}

/** Colunas da comparação; cada rótulo de métrica carrega a pergunta que responde. */
const COLUMNS: readonly Column<Row>[] = [
  { key: "id", title: "#", render: (row) => row.scenarioId },
  { key: "label", title: "hardware", render: (row) => <span className="whitespace-nowrap font-medium">{row.label}</span> },
  {
    key: "load",
    title: "carga",
    render: (row) => <span className="whitespace-nowrap">{`${row.concurrency} × ${formatContext(row.contextLen)}`}</span>,
  },
  {
    key: "agg",
    title: <HintLabel hint={METRIC_HINT.aggregate}>vazão agreg.</HintLabel>,
    align: "right",
    sortValue: (row) => row.aggregate,
    render: (row) => <span className="font-semibold text-kumo-strong">{formatNumber(row.aggregate)}</span>,
  },
  {
    key: "cost",
    title: <HintLabel hint={METRIC_HINT.costPerM}>US$/1M tok</HintLabel>,
    align: "right",
    sortValue: (row) => row.costPerM,
    render: (row) => <span className="font-semibold text-kumo-strong">{formatNumber(row.costPerM, 3)}</span>,
  },
  {
    key: "watt",
    title: <HintLabel hint={METRIC_HINT.perWatt}>tok/s/W</HintLabel>,
    align: "right",
    sortValue: (row) => row.perWatt,
    render: (row) => formatNumber(row.perWatt, 2),
  },
  {
    key: "rps",
    title: <HintLabel hint={METRIC_HINT.requests}>req/s</HintLabel>,
    align: "right",
    render: (row) => formatNumber(row.requests, 2),
  },
  {
    key: "maxc",
    title: <HintLabel hint={METRIC_HINT.maxConc}>conc. máx.</HintLabel>,
    align: "right",
    render: (row) => <span className="whitespace-nowrap">{row.maxConc} sessões</span>,
  },
  {
    key: "kv",
    title: "cache ocupado",
    align: "right",
    render: (row) => (
      <span className={row.kvUsedPct > KV_ALERT_PCT ? "font-semibold text-kumo-danger" : ""}>
        {formatNumber(row.kvUsedPct)}%
      </span>
    ),
  },
  {
    key: "itl",
    title: <HintLabel hint={METRIC_HINT.itl}>ITL p95</HintLabel>,
    align: "right",
    render: (row) => <span className="whitespace-nowrap">{formatNumber(row.itl, 2)} ms</span>,
  },
  {
    key: "bw",
    title: <HintLabel hint={METRIC_HINT.bandwidth}>banda</HintLabel>,
    align: "right",
    render: (row) => `${formatNumber(row.bandwidthPct)}%`,
  },
  { key: "verdict", title: "desfecho", render: (row) => <VerdictBadge verdict={row.verdict} /> },
];

/** Capacidade, eficiência e custo por token — as métricas de dimensionamento. */
export function Scale(): React.ReactElement {
  const reference = snapshot.capacity.find((c) => c.scenarioId === REFERENCE_SCENARIO_ID);
  const refStats = snapshot.stats.find((s) => s.scenarioId === REFERENCE_SCENARIO_ID);
  const refScenario = snapshot.scenarios.find((s) => s.id === REFERENCE_SCENARIO_ID);
  const refRun = snapshot.runs.find((r) => r.id === refScenario?.runId);

  const rows = useMemo<Row[]>(
    () =>
      snapshot.capacity.map((cap) => {
        const scenario = snapshot.scenarios.find((s) => s.id === cap.scenarioId);
        const run = snapshot.runs.find((r) => r.id === scenario?.runId);
        const stats = snapshot.stats.find((s) => s.scenarioId === cap.scenarioId);
        return {
          scenarioId: cap.scenarioId,
          label: `${run?.instanceType ?? "—"} · ${run?.gpuCount ?? 0}× ${run?.gpuModel ?? "—"}`,
          concurrency: scenario?.concurrency ?? 0,
          contextLen: scenario?.contextLen ?? 0,
          verdict: stats?.verdict ?? "fails",
          aggregate: cap.aggregateTokensPerSec,
          requests: cap.requestsPerSec,
          maxConc: cap.maxConcurrencyAtTarget,
          kvUsedPct: cap.kvUsedPct,
          perWatt: cap.tokensPerWatt,
          costPerM: cap.costPerMillionTokensUsd,
          itl: cap.itlP95Ms,
          bandwidthPct: cap.bandwidthEfficiencyPct,
        };
      }),
    [],
  );

  /** Vazão agregada contra concorrência: mostra onde a capacidade satura. */
  const scaleCurve = useMemo<SeriesPoint[]>(() => {
    const points: SeriesPoint[] = [];
    for (const run of snapshot.runs) {
      const label = `${run.instanceType} (${run.gpuCount}× ${run.gpuModel})`;
      const scenarios = snapshot.scenarios
        .filter((s) => s.runId === run.id && s.loadProfile === "chat" && s.radixCache)
        .sort((a, b) => a.concurrency - b.concurrency);
      // Uma curva de um ponto só não mostra saturação nenhuma.
      if (scenarios.length < 2) {
        continue;
      }
      for (const scenario of scenarios) {
        const cap = snapshot.capacity.find((c) => c.scenarioId === scenario.id);
        if (cap === undefined) {
          continue;
        }
        points.push({ x: scenario.concurrency, y: Number(cap.aggregateTokensPerSec.toFixed(0)), series: label });
      }
    }
    return points;
  }, []);

  const title = `Capacidade do candidato — ${refRun?.instanceType ?? "—"} · ${refRun?.gpuCount ?? 0}× ${refRun?.gpuModel ?? "—"} · ${refScenario?.concurrency ?? 0} sessões a ${formatContext(refScenario?.contextLen ?? 0)}`;

  return (
    <Page>
      <Panel
        title={title}
        description="Passe o ponteiro sobre cada rótulo para ver que pergunta de dimensionamento ele responde. Nenhuma destas métricas aparece nas telas de latência — e nenhuma delas é dispensável para decidir a compra."
        bodyClassName="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2 xl:grid-cols-4"
      >
        <Stat
          label={<HintLabel hint={METRIC_HINT.aggregate}>vazão agregada</HintLabel>}
          value={reference?.aggregateTokensPerSec ?? 0}
          suffix="tok/s"
          caption="capacidade total do servidor"
          tone="success"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.costPerM}>custo por 1M tokens</HintLabel>}
          value={reference?.costPerMillionTokensUsd ?? 0}
          precision={3}
          prefix="US$"
          caption="na instância de bancada"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.perWatt}>tokens por watt</HintLabel>}
          value={reference?.tokensPerWatt ?? 0}
          precision={2}
          suffix="tok/s/W"
          caption="eficiência energética"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.maxConc}>concorrência máxima no alvo</HintLabel>}
          value={reference?.maxConcurrencyAtTarget ?? 0}
          suffix="sessões"
          caption="derivada da banda disponível"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.requests}>requisições/s</HintLabel>}
          value={reference?.requestsPerSec ?? 0}
          precision={2}
          suffix="req/s"
          size="compact"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.kv}>capacidade de cache</HintLabel>}
          value={(reference?.kvCapacityTokens ?? 0) / 1_000}
          suffix="k tokens"
          caption={`${formatNumber(reference?.kvUsedPct ?? 0)}% ocupado neste cenário`}
          size="compact"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.itl}>latência entre tokens</HintLabel>}
          value={reference?.itlP95Ms ?? 0}
          precision={2}
          suffix="ms (p95)"
          size="compact"
        />
        <Stat
          label={<HintLabel hint={METRIC_HINT.bandwidth}>eficiência de banda</HintLabel>}
          value={reference?.bandwidthEfficiencyPct ?? 0}
          precision={1}
          suffix="%"
          caption={`${formatNumber(reference?.effectiveBandwidthTbs ?? 0, 2)} TB/s exercidos`}
          size="compact"
        />
      </Panel>

      <Panel
        title="Vazão agregada × concorrência — onde a capacidade satura"
        description="Esta curva é distinta da de latência: ela sobe enquanto o servidor absorve mais carga e achata quando satura. O ponto de achatamento é a capacidade real do hardware — e ele não coincide com o ponto em que a latência individual passa a reprovar."
      >
        <ConcurrencyCurveChart
          data={scaleCurve}
          targetValue={TARGET_TOKENS_PER_SEC * (refScenario?.concurrency ?? 64)}
          targetLabel="capacidade no alvo"
          yTitle="vazão agregada (tokens por segundo)"
          height={340}
        />
      </Panel>

      <Panel title="Comparação entre cenários medidos">
        <DataTable columns={COLUMNS} rows={rows} rowKey={(row) => row.scenarioId} />
      </Panel>

      <Panel title="Por que estas métricas, e não só a latência">
        <p className="text-sm text-kumo-subtle">
          Dois candidatos podem entregar os mesmos {TARGET_TOKENS_PER_SEC} tokens por segundo por requisição e ainda
          assim serem escolhas muito diferentes: um atende o dobro de sessões, ou consome metade da energia, ou custa
          um terço por milhão de tokens. A latência responde se o serviço é bom; a capacidade e o custo por token
          respondem se ele é comprável. O percentil 95 de latência — {formatNumber(refStats?.tokensPerSecP95 ?? 0)}{" "}
          tok/s — não distingue nada disso sozinho.
        </p>
      </Panel>
    </Page>
  );
}
